import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, InputBase, Paper } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import styled from "styled-components";

import Table from "../../../common/table";
import FullScreenDialog from "../../../common/FullScreenDialog";
import DailyStepForm from "../dayplan/components/dailyStep/DailyStepForm";
import { updateDailyStep } from "../../../../database/dailyStep/updateDailyStep";
import DB_PATH from "../../../../database/DB_PATH";
import { firestore } from "../../../../database/firebase";
import { Author } from "../../../../types/Author";
import { DayPlan } from "../../../../types/DayPlan";
import { DailyStep } from "../../../../types/DailyStep";
import { Plan } from "../../../../types/Plan";
import StoreUser from "../../../../types/StoreUser";

type Props = {
  user?: StoreUser;
  plans: Plan[];
  authors: Author[];
  loading?: boolean;
};

type StepTableItem = DailyStep & {
  authorDescription: string;
  dayPlanDescription: string;
  dayPlanPosition: number;
  dayPlanTitle: string;
  pathLabel: string;
  planDescription: string;
  planPosition: number;
  planTitle: string;
};

const STEP_TAB_CONFIG = [
  {
    columnTitle: "Path",
    dataField: ["pathLabel"],
  },
  {
    columnTitle: "Plan",
    dataField: ["planTitle"],
  },
  {
    columnTitle: "Day",
    dataField: ["dayPlanTitle"],
  },
  {
    columnTitle: "Step",
    dataField: ["title"],
  },
  {
    columnTitle: "Subtitle",
    dataField: ["subtitle"],
  },
];

const Card = styled(Paper)`
  padding: 24px;
  border-radius: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 22px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 13px;
  font-weight: 700;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  color: #6b7280;
  font-size: 14px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  min-width: 260px;

  svg {
    color: #9ca3af;
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const SearchInput = styled(InputBase)`
  flex: 1;
  font-size: 14px;

  .MuiInputBase-input {
    padding: 0;
    color: #111827;

    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }
  }
`;

const EmptyState = styled.div`
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
  padding: 24px;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
`;

const EditorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EditorTitle = styled.h3`
  margin: 0;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
`;

const EditorSubtitle = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 14px;
`;

const EditorHint = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 12px;
`;

const EditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const getSortablePosition = (value?: number | string) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : Number.MAX_SAFE_INTEGER;
};

const getDisplayPosition = (value?: number | string) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : "-";
};

const buildPathLabel = (
  planPosition?: number | string,
  dayPlanPosition?: number | string,
  stepPosition?: number | string
) =>
  `${getDisplayPosition(planPosition)}.${getDisplayPosition(dayPlanPosition)}.${getDisplayPosition(stepPosition)}`;

const toPersistedStep = ({
  actionItems,
  authorID,
  authorName,
  backgroundImageURL,
  communityId,
  dayPlanID,
  description,
  id,
  images,
  isCompleted,
  planID,
  position,
  questions,
  quote,
  reelVoiceAudioUrl,
  reelSegments,
  reelVoiceAuthorReference,
  reelVoiceTimeline,
  reference,
  sectionTitle,
  slideAuthorReference,
  slides,
  subtitle,
  text,
  title,
  type,
  videoURL,
  xpReward,
}: DailyStep): DailyStep => ({
  actionItems,
  authorID,
  authorName,
  backgroundImageURL,
  communityId,
  dayPlanID,
  description,
  id,
  images,
  isCompleted,
  planID,
  position,
  questions,
  quote,
  reelVoiceAudioUrl,
  reelSegments,
  reelVoiceAuthorReference,
  reelVoiceTimeline,
  reference,
  sectionTitle,
  slideAuthorReference,
  slides,
  subtitle,
  text,
  title,
  type,
  videoURL,
  xpReward,
});

const PlanStepsTab = ({ user, plans, authors, loading = false }: Props) => {
  const [dayPlans, setDayPlans] = useState<DayPlan[]>([]);
  const [dailySteps, setDailySteps] = useState<DailyStep[]>([]);
  const [isDayPlansLoading, setIsDayPlansLoading] = useState(true);
  const [isStepsLoading, setIsStepsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!user?.communityId) {
      setDayPlans([]);
      setDailySteps([]);
      setIsDayPlansLoading(false);
      setIsStepsLoading(false);
      return undefined;
    }

    setIsDayPlansLoading(true);
    setIsStepsLoading(true);

    const unsubscribeDayPlans = firestore
      .collection(DB_PATH.PLAN_DAY)
      .where("communityId", "==", user.communityId)
      .onSnapshot(
        (snapshot) => {
          setDayPlans(
            snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                } as DayPlan)
            )
          );
          setIsDayPlansLoading(false);
        },
        (error) => {
          console.error("Error fetching day plans:", error);
          setDayPlans([]);
          setIsDayPlansLoading(false);
        }
      );

    const unsubscribeSteps = firestore
      .collection(DB_PATH.PLAN_STEP)
      .where("communityId", "==", user.communityId)
      .onSnapshot(
        (snapshot) => {
          setDailySteps(
            snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                } as DailyStep)
            )
          );
          setIsStepsLoading(false);
        },
        (error) => {
          console.error("Error fetching steps:", error);
          setDailySteps([]);
          setIsStepsLoading(false);
        }
      );

    return () => {
      unsubscribeDayPlans();
      unsubscribeSteps();
    };
  }, [user?.communityId]);

  const plansById = useMemo(
    () => new Map(plans.map((plan) => [plan.id, plan])),
    [plans]
  );

  const dayPlansById = useMemo(
    () => new Map(dayPlans.map((dayPlan) => [dayPlan.id, dayPlan])),
    [dayPlans]
  );

  const authorsById = useMemo(
    () => new Map(authors.map((author) => [author.id, author])),
    [authors]
  );

  const stepRows = useMemo(() => {
    return dailySteps
      .map((step) => {
        const dayPlan = dayPlansById.get(step.dayPlanID);
        const plan = plansById.get(step.planID || dayPlan?.planID || "");
        const author =
          authorsById.get(step.authorID || plan?.authorId || "") || null;

        return {
          ...step,
          authorDescription:
            author?.description || author?.shortDescription || "",
          authorID: step.authorID || plan?.authorId || author?.id || "",
          authorName: step.authorName || plan?.authorName || author?.fullName || "",
          communityId: step.communityId || plan?.communityId || user?.communityId || "",
          dayPlanDescription: dayPlan?.description || "",
          dayPlanPosition: dayPlan?.position ?? Number.MAX_SAFE_INTEGER,
          dayPlanTitle: dayPlan?.title || "Untitled day",
          pathLabel: buildPathLabel(plan?.position, dayPlan?.position, step.position),
          planDescription: plan?.description || "",
          planPosition: plan?.position ?? Number.MAX_SAFE_INTEGER,
          planTitle: plan?.title || "Untitled plan",
        } as StepTableItem;
      })
      .sort((left, right) => {
        const byPlanPosition =
          getSortablePosition(left.planPosition) -
          getSortablePosition(right.planPosition);
        if (byPlanPosition !== 0) return byPlanPosition;

        const byPlanTitle = left.planTitle.localeCompare(right.planTitle);
        if (byPlanTitle !== 0) return byPlanTitle;

        const byDayPosition =
          getSortablePosition(left.dayPlanPosition) -
          getSortablePosition(right.dayPlanPosition);
        if (byDayPosition !== 0) return byDayPosition;

        const byDayTitle = left.dayPlanTitle.localeCompare(right.dayPlanTitle);
        if (byDayTitle !== 0) return byDayTitle;

        const byStepPosition =
          getSortablePosition(left.position) - getSortablePosition(right.position);
        if (byStepPosition !== 0) return byStepPosition;

        return (left.title || "").localeCompare(right.title || "");
      });
  }, [authorsById, dailySteps, dayPlansById, plansById, user?.communityId]);

  const filteredStepRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return stepRows;
    return stepRows.filter((step) =>
      [step.pathLabel, step.planTitle, step.dayPlanTitle, step.title, step.subtitle]
        .some((field) => field?.toLowerCase().includes(query))
    );
  }, [searchQuery, stepRows]);

  const selectedStep = useMemo(
    () => stepRows.find((step) => step.id === selectedStepId) || null,
    [selectedStepId, stepRows]
  );

  const selectedStepIndex = useMemo(
    () => stepRows.findIndex((step) => step.id === selectedStepId),
    [selectedStepId, stepRows]
  );

  const siblingSteps = useMemo(() => {
    if (!selectedStep) return [];
    return stepRows.filter((step) => step.dayPlanID === selectedStep.dayPlanID);
  }, [selectedStep, stepRows]);

  useEffect(() => {
    if (selectedStepId && !isStepsLoading && !stepRows.some((step) => step.id === selectedStepId)) {
      setSelectedStepId(null);
    }
  }, [isStepsLoading, selectedStepId, stepRows]);

  useEffect(() => {
    if (currentPage > 0 && currentPage * 10 >= stepRows.length) {
      setCurrentPage(0);
    }
  }, [currentPage, stepRows.length]);

  const onEditIconClicked = (step: StepTableItem) => {
    setSelectedStepId(step.id);
  };

  const onGoToRelativeStep = (direction: -1 | 1) => {
    if (selectedStepIndex < 0) return;

    const targetStep = stepRows[selectedStepIndex + direction];
    if (targetStep) {
      setSelectedStepId(targetStep.id);
    }
  };

  const onRemoveStep = async (step: StepTableItem) => {
    const confirmed =
      typeof window === "undefined"
        ? true
        : window.confirm(
            `Delete this step${step.title ? `: "${step.title}"` : ""}? This action cannot be undone.`
          );

    if (!confirmed) {
      return;
    }

    const stepIndex = stepRows.findIndex((row) => row.id === step.id);
    const nextSelectedStepId =
      selectedStepId === step.id
        ? stepRows[stepIndex + 1]?.id || stepRows[stepIndex - 1]?.id || null
        : selectedStepId;

    try {
      await firestore.collection(DB_PATH.PLAN_STEP).doc(step.id).delete();
      setSelectedStepId(nextSelectedStepId);
    } catch (error) {
      console.error("Error deleting step:", error);
    }
  };

  const onSaveStep = async (step: DailyStep) => {
    const currentStep = stepRows.find((row) => row.id === step.id) || selectedStep;

    if (!currentStep) {
      return;
    }

    await updateDailyStep({
      dailyStep: toPersistedStep({
        ...currentStep,
        ...step,
        authorID: step.authorID || currentStep.authorID || "",
        authorName: step.authorName || currentStep.authorName || "",
        communityId:
          step.communityId || currentStep.communityId || user?.communityId || "",
        dayPlanID: step.dayPlanID || currentStep.dayPlanID,
        id: step.id || currentStep.id,
        planID: step.planID || currentStep.planID,
      }),
    });
  };

  const isLoading = loading || isDayPlansLoading || isStepsLoading;
  const canGoBack = selectedStepIndex > 0;
  const canGoForward =
    selectedStepIndex >= 0 && selectedStepIndex < stepRows.length - 1;

  if (isLoading) {
    return (
      <Card elevation={1}>
        <LoadingState>
          <CircularProgress size={24} />
          Loading all plan steps...
        </LoadingState>
      </Card>
    );
  }

  return (
    <>
      <Card elevation={1}>
        <Header>
          <TitleBlock>
            <Title>All Steps</Title>
            <Subtitle>
              Review every step in one place, open the editor directly from the
              table, then move through them one by one without going back into
              each day plan.
            </Subtitle>
          </TitleBlock>
          <CountBadge>{filteredStepRows.length}</CountBadge>
        </Header>

        <SearchBox>
          <SearchIcon />
          <SearchInput
            placeholder="Search by path, plan, day, step, or subtitle..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0);
            }}
          />
        </SearchBox>

        {filteredStepRows.length === 0 ? (
          <EmptyState>
            {searchQuery.trim()
              ? `No steps match "${searchQuery.trim()}".`
              : "No steps found for this community yet. Create or import a plan with day plans first."}
          </EmptyState>
        ) : (
          <Table
            canBeEdited
            canBeRemoved
            currentPage={currentPage}
            dataset={filteredStepRows}
            onEditIconClicked={onEditIconClicked}
            onRemoveIconClicked={onRemoveStep}
            tabConfig={STEP_TAB_CONFIG}
            updatePageIndex={setCurrentPage}
          />
        )}
      </Card>

      <FullScreenDialog
        isOpen={!!selectedStep}
        onClose={() => setSelectedStepId(null)}
        title="Step Editor"
      >
        {selectedStep && (
          <>
            <EditorHeader>
              <EditorMeta>
                <EditorTitle>{selectedStep.title || "Untitled step"}</EditorTitle>
                <EditorSubtitle>
                  {selectedStep.planTitle} / {selectedStep.dayPlanTitle} / Step{" "}
                  {selectedStepIndex + 1} of {stepRows.length}
                </EditorSubtitle>
                <EditorHint>
                  Save before moving to another step. Navigation keeps the editor
                  open and follows the same order as the table.
                </EditorHint>
              </EditorMeta>

              <EditorActions>
                <Button
                  color="secondary"
                  onClick={() => onRemoveStep(selectedStep)}
                  variant="outlined"
                >
                  Delete
                </Button>
                <Button
                  disabled={!canGoBack}
                  onClick={() => onGoToRelativeStep(-1)}
                  variant="outlined"
                >
                  Previous
                </Button>
                <Button
                  color="primary"
                  disabled={!canGoForward}
                  onClick={() => onGoToRelativeStep(1)}
                  variant="contained"
                >
                  Next
                </Button>
              </EditorActions>
            </EditorHeader>

            <DailyStepForm
              item={selectedStep}
              mode="edit"
              planID={selectedStep.planID}
              dayPlanID={selectedStep.dayPlanID}
              onCreateItem={onSaveStep}
              onEditItem={onSaveStep}
              onRemoveItem={() => onRemoveStep(selectedStep)}
              onToggleFullScreenDialog={() => setSelectedStepId(null)}
              authorID={selectedStep.authorID}
              authorDescription={selectedStep.authorDescription}
              authorName={selectedStep.authorName}
              communityId={selectedStep.communityId}
              dayPlanDescription={selectedStep.dayPlanDescription}
              dayPlanTitle={selectedStep.dayPlanTitle}
              planDescription={selectedStep.planDescription}
              planTitle={selectedStep.planTitle}
              steps={siblingSteps}
            />
          </>
        )}
      </FullScreenDialog>
    </>
  );
};

export default PlanStepsTab;
